import { api } from '@sd/contracts';
import { ClientModule } from './context';
import { setLinkPreviews } from './merge';
import { ObjectUrlCache } from './objectUrls';

/**
 * Preview images no card shows any more that are kept for reuse, least recently used first;
 * beyond this the oldest object URL is revoked. Each is the server's small WebP (a few KB), and
 * cards still on screen are never revoked. Kept apart from profile photos (avatars.ts): neither
 * evicts the other's entries.
 */
export const IDLE_LINK_THUMBNAILS = 48;
/** Requests at once, besides the photos' own six: a channel full of links does not hold up other requests. */
const MAX_FETCHES = 4;

/** Link preview cards: their images, fetched from the company server like file previews, and the author's removal. */
export class LinkPreviews extends ClientModule {
  private readonly thumbnails = new ObjectUrlCache({ idle: IDLE_LINK_THUMBNAILS, maxFetches: MAX_FETCHES, now: () => this.ctx.now().getTime() });

  /** Object URL for a card's image (fetched with this session's credentials); `releaseThumbnail` once it is no longer shown. */
  thumbnailUrl(previewId: string): Promise<string> {
    return this.thumbnails.get(previewId, async () => (await this.api.call(api.linkPreviews.thumbnail, { params: { previewId } })).blob());
  }

  releaseThumbnail(previewId: string) {
    this.thumbnails.release(previewId);
  }

  revokeAll() {
    this.thumbnails.clear();
  }

  /** The author takes a card off their message for everyone; the other members get it through the event. */
  async remove(conversationId: string, messageId: string, previewId: string): Promise<void> {
    const res = await this.api.call(api.linkPreviews.remove, { params: { conversationId, messageId, previewId } });
    this.set((s) => {
      const t = s.timelines[conversationId];
      return t ? { timelines: { ...s.timelines, [conversationId]: { ...t, messages: setLinkPreviews(t.messages, res.messageId, res.linkPreviews) } } } : {};
    });
  }
}
